import { useQuery } from '@tanstack/react-query';
import { axios } from '@/src/lib/axios/axios';
import { QueryConfig } from '@/src/lib/react-query/react-query';
import {
  MESSAGING_API_BASE,
  messagingQueryKeys,
  DEFAULT_MEMBERS_LIMIT,
} from '../config';
import { EligibleUsersResponse, UserInfo } from '../types/backend';

export type EligibleUsersQuery = {
  channelId: number;
  search?: string;
  limit?: number;
  offset?: number;
};

const getEligibleUsers = async (
  params: EligibleUsersQuery
): Promise<EligibleUsersResponse> => {
  const {
    channelId,
    search,
    limit = DEFAULT_MEMBERS_LIMIT,
    offset = 0,
  } = params;

  const queryParams = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  if (search) {
    queryParams.append('search', search);
  }

  return axios.get(
    `${MESSAGING_API_BASE}/channels/${channelId}/eligible-users?${queryParams}`
  );
};

/**
 * Fetch all eligible users by paginating through all available records
 * until the total count is reached
 */
const getAllEligibleUsers = async (
  params: EligibleUsersQuery
): Promise<EligibleUsersResponse> => {
  const { channelId, search, limit = DEFAULT_MEMBERS_LIMIT } = params;

  let allUsers: UserInfo[] = [];
  let offset = 0;
  let totalCount = 0;

  // Keep fetching until we have all users
  while (true) {
    const response = await getEligibleUsers({
      channelId,
      search,
      limit,
      offset,
    });

    allUsers = [...allUsers, ...response.rows];
    totalCount = response.count;

    // Break if we've fetched all users
    if (allUsers.length >= totalCount) {
      break;
    }

    // Move to next page
    offset += limit;
  }

  return {
    rows: allUsers,
    count: totalCount,
  };
};

type QueryFnType = typeof getAllEligibleUsers;

type UseEligibleUsersOptions = {
  channelId: number;
  search?: string;
  limit?: number;
  fetchAll?: boolean; // If true, fetch all records; if false, fetch single page
  offset?: number;
  config?: QueryConfig<QueryFnType>;
};

export const useEligibleUsers = ({
  channelId,
  search,
  limit,
  fetchAll = true,
  offset,
  config,
}: UseEligibleUsersOptions) => {
  return useQuery({
    queryKey: [
      ...messagingQueryKeys.eligibleUsers(channelId, search),
      { fetchAll, limit, offset },
    ],
    queryFn: () =>
      fetchAll
        ? getAllEligibleUsers({ channelId, search, limit })
        : getEligibleUsers({ channelId, search, limit, offset }),
    staleTime: 30000, // Cache for 30 seconds
    enabled: channelId > 0,
    ...config,
  });
};
